import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { authApi } from '../../services/api';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultUser = {
    email: '',
    role: 'viewer',
    modules: [],
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await authApi.getUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    setEditingUser(user ? { ...user } : { ...defaultUser });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleSave = async () => {
    try {
      if (editingUser.uid) {
        await authApi.updateUser(editingUser.uid, editingUser);
      } else {
        await authApi.createUser(editingUser);
      }
      setSnackbar({ open: true, message: 'User saved successfully!', severity: 'success' });
      handleCloseDialog();
      loadUsers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save user.', severity: 'error' });
    }
  };

  const handleDelete = async (uid) => {
    if (!window.confirm('Delete this user? They will lose access to the dashboard.')) return;
    try {
      await authApi.deleteUser(uid);
      setSnackbar({ open: true, message: 'User deleted.', severity: 'success' });
      loadUsers();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete user.', severity: 'error' });
    }
  };

  const handleModuleToggle = (module) => {
    const modules = editingUser.modules || [];
    if (modules.includes(module)) {
      setEditingUser({
        ...editingUser,
        modules: modules.filter((m) => m !== module),
      });
    } else {
      setEditingUser({
        ...editingUser,
        modules: [...modules, module],
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          User Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add User
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Module Access</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" py={3}>
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.uid}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user.email}
                        {user.email === 'david.zickafoose@gmail.com' && (
                          <Chip label="Owner" size="small" color="primary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        size="small"
                        color={user.role === 'admin' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {user.modules?.includes('crl') && (
                          <Chip label="CRL" size="small" variant="outlined" color="primary" />
                        )}
                        {user.modules?.includes('insider') && (
                          <Chip label="Insider" size="small" variant="outlined" color="secondary" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell align="right">
                      {user.email !== 'david.zickafoose@gmail.com' && (
                        <>
                          <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(user.uid)}>
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser?.uid ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={editingUser?.email || ''}
            onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
            disabled={!!editingUser?.uid}
            sx={{ mt: 2, mb: 3 }}
            helperText={editingUser?.uid ? 'Email cannot be changed' : 'User must sign in with this Google account'}
          />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={editingUser?.role || 'viewer'}
              label="Role"
              onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
            >
              <MenuItem value="viewer">Viewer (Read-only)</MenuItem>
              <MenuItem value="admin">Admin (Full access)</MenuItem>
            </Select>
          </FormControl>

          <Typography gutterBottom>Module Access</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingUser?.modules?.includes('crl') || false}
                  onChange={() => handleModuleToggle('crl')}
                />
              }
              label="FDA CRL Analysis"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editingUser?.modules?.includes('insider') || false}
                  onChange={() => handleModuleToggle('insider')}
                />
              }
              label="Insider Trading"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!editingUser?.email || (editingUser?.modules?.length === 0)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UserManagement;
